/**
 * Alibaba Cloud - DashScope AI, Function Compute, TableStore, OSS
 */
import axios from 'axios'
import { AlertCircle, Check, CheckCircle, ChevronDown, ChevronRight, ExternalLink, Loader } from 'lucide-react'
import { useEffect, useState } from 'react'
import { API_URL } from '../../../config/api'

interface AlibabaResource {
  id: string
  name: string
  type: string
  status: 'active' | 'inactive' | 'deploying' | 'error'
  endpoint?: string
}

interface AlibabaStatus {
  connected: boolean
  access_key_id?: string
  region?: string
  resources: AlibabaResource[]
}

const ALIBABA_RESOURCES = [
  { id: 'fc', label: 'Function Compute', desc: 'Serverless compute (Node.js, Python, Java)', icon: '⚡' },
  { id: 'dashscope', label: 'DashScope AI', desc: 'Qwen LLM, image generation, speech', icon: '🤖' },
  { id: 'tablestore', label: 'TableStore (NoSQL)', desc: 'Wide-column NoSQL database', icon: '🗄️' },
  { id: 'oss', label: 'Object Storage (OSS)', desc: 'S3-compatible object storage', icon: '📦' },
  { id: 'redis', label: 'ApsaraDB for Redis', desc: 'Managed Redis cache', icon: '⚡' },
]

const ALIBABA_REGIONS = [
  'cn-hangzhou (Hangzhou)', 'cn-shanghai (Shanghai)', 'cn-beijing (Beijing)',
  'ap-southeast-1 (Singapore)', 'ap-northeast-1 (Tokyo)', 'eu-central-1 (Frankfurt)',
  'us-east-1 (Virginia)', 'us-west-1 (Silicon Valley)',
]

const PRICING = [
  { service: 'Function Compute', price: '$0.000016/GB-s', note: '400K GB-s free/month' },
  { service: 'DashScope (Qwen)', price: '$0.0014/1000 tokens', note: 'Qwen-Max model' },
  { service: 'TableStore', price: '$0.00014/10K read', note: 'First 25GB free' },
  { service: 'OSS', price: '$0.020/GB/month', note: '5GB free/month' },
]

export default function AlibabaPage() {
  const [status, setStatus] = useState<AlibabaStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [deploying, setDeploying] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [openSection, setOpenSection] = useState<string | null>(null)
  const [selectedResources, setSelectedResources] = useState<string[]>(['fc', 'dashscope', 'tablestore', 'oss'])
  const [form, setForm] = useState({ access_key_id: '', access_key_secret: '', region: 'ap-southeast-1 (Singapore)' })

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('token')
      const resp = await axios.get(`${API_URL}/api/aicloud/platforms/alibaba`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setStatus(resp.data as AlibabaStatus)
    } catch {
      setStatus({ connected: false, resources: [] })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void fetchStatus() }, [])

  const handleConnect = async () => {
    if (!form.access_key_id || !form.access_key_secret) { setError('Access Key ID and Secret required'); return }
    setSaving(true); setError(null)
    try {
      const token = localStorage.getItem('token')
      await axios.post(`${API_URL}/api/aicloud/platforms/alibaba`, form, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setSuccess('Alibaba Cloud connected')
      setShowForm(false)
      void fetchStatus()
    } catch (e) {
      setError((e as { response?: { data?: { detail?: string } } }).response?.data?.detail ?? 'Connection failed')
    } finally { setSaving(false) }
  }

  const handleDeploy = async () => {
    setDeploying(true); setError(null)
    try {
      const token = localStorage.getItem('token')
      await axios.post(`${API_URL}/api/aicloud/platforms/alibaba/deploy`, { resources: selectedResources }, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setSuccess('Deployment started')
      void fetchStatus()
    } catch (e) {
      setError((e as { response?: { data?: { detail?: string } } }).response?.data?.detail ?? 'Deploy failed')
    } finally { setDeploying(false) }
  }

  const handleDisconnect = async () => {
    try {
      const token = localStorage.getItem('token')
      await axios.delete(`${API_URL}/api/aicloud/platforms/alibaba`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setSuccess('Disconnected')
      void fetchStatus()
    } catch { setError('Failed') }
  }

  const toggleResource = (id: string) => {
    setSelectedResources(prev =>
      prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]
    )
  }

  if (loading) {return <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Loading...</div>}

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: '#FF6A00', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>
            🟠
          </div>
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '2px' }}>Alibaba Cloud</h2>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>DashScope AI (Qwen), Function Compute, TableStore, OSS</p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {status?.connected && (
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#10b981' }}>
              <CheckCircle size={13} /> Connected
            </span>
          )}
          <a href="https://www.alibabacloud.com/free" target="_blank" rel="noreferrer"
            style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#3b82f6', textDecoration: 'none' }}>
            <ExternalLink size={12} /> Alibaba Cloud
          </a>
        </div>
      </div>

      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', color: '#ef4444', fontSize: '13px', marginBottom: '16px' }}>
          <AlertCircle size={14} /> {error}
        </div>
      )}
      {success && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: '8px', color: '#10b981', fontSize: '13px', marginBottom: '16px' }}>
          <Check size={14} /> {success}
        </div>
      )}

      {!status?.connected && !showForm && (
        <div style={{ padding: '32px', textAlign: 'center', background: 'var(--card-bg)', border: '1px dashed var(--border-color)', borderRadius: '10px', marginBottom: '20px' }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>🟠</div>
          <p style={{ fontSize: '14px', fontWeight: 600, marginBottom: '6px' }}>Alibaba Cloud not connected</p>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
            Connect your Alibaba Cloud account to deploy Qwen AI, Function Compute, and more.
          </p>
          <button onClick={() => setShowForm(true)}
            style={{ padding: '10px 20px', background: '#FF6A00', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
            Connect Alibaba Cloud
          </button>
        </div>
      )}

      {showForm && (
        <div style={{ padding: '20px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', marginBottom: '20px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px' }}>Connect Alibaba Cloud</h3>

          <div style={{ marginBottom: '16px', padding: '12px', background: 'rgba(255,106,0,0.06)', border: '1px solid rgba(255,106,0,0.2)', borderRadius: '8px', fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            <strong style={{ color: 'var(--text-primary)' }}>How to get Access Keys:</strong><br />
            1. Go to <a href="https://ram.console.aliyun.com/manage/ak" target="_blank" rel="noreferrer" style={{ color: '#3b82f6' }}>Alibaba Cloud → RAM → AccessKey Management</a><br />
            2. Click &quot;Create AccessKey&quot;<br />
            3. Copy Access Key ID and Access Key Secret
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>Access Key ID *</label>
              <input type="text" placeholder="LTAI5t..."
                value={form.access_key_id}
                onChange={e => setForm(f => ({ ...f, access_key_id: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '13px', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>Access Key Secret *</label>
              <input type="password" placeholder="Enter secret"
                value={form.access_key_secret}
                onChange={e => setForm(f => ({ ...f, access_key_secret: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '13px', boxSizing: 'border-box' }} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>Region</label>
              <select value={form.region} onChange={e => setForm(f => ({ ...f, region: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '13px' }}>
                {ALIBABA_REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => { void handleConnect() }} disabled={saving}
              style={{ padding: '8px 16px', background: '#FF6A00', color: 'white', border: 'none', borderRadius: '6px', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 600, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Connecting...' : 'Connect'}
            </button>
            <button onClick={() => setShowForm(false)}
              style={{ padding: '8px 16px', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-color)', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {status?.connected && (
        <>
          <div style={{ padding: '20px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '14px' }}>Select Resources to Deploy</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
              {ALIBABA_RESOURCES.map(r => (
                <label key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: selectedResources.includes(r.id) ? 'rgba(255,106,0,0.06)' : 'var(--body-bg)', border: `1px solid ${selectedResources.includes(r.id) ? 'rgba(255,106,0,0.3)' : 'var(--border-color)'}`, borderRadius: '8px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={selectedResources.includes(r.id)} onChange={() => toggleResource(r.id)} />
                  <span style={{ fontSize: '16px' }}>{r.icon}</span>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600 }}>{r.label}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{r.desc}</div>
                  </div>
                </label>
              ))}
            </div>
            <button onClick={() => { void handleDeploy() }} disabled={deploying || selectedResources.length === 0}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 20px', background: '#FF6A00', color: 'white', border: 'none', borderRadius: '8px', cursor: deploying ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 600, opacity: deploying ? 0.6 : 1 }}>
              {deploying ? <><Loader size={14} /> Deploying...</> : 'Deploy Selected Resources'}
            </button>
          </div>

          {status.resources.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '10px', color: 'var(--text-secondary)' }}>Deployed Resources</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {status.resources.map(r => (
                  <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '8px' }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: r.status === 'active' ? '#10b981' : r.status === 'deploying' ? '#f59e0b' : '#ef4444', flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '13px', fontWeight: 600 }}>{r.name}</div>
                      {r.endpoint && <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{r.endpoint}</div>}
                    </div>
                    <span style={{ fontSize: '11px', padding: '2px 8px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '4px', color: 'var(--text-secondary)' }}>{r.type}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button onClick={() => { void handleDisconnect() }}
            style={{ padding: '8px 14px', background: 'transparent', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>
            Disconnect Alibaba Cloud
          </button>
        </>
      )}

      <div style={{ marginTop: '20px' }}>
        <button onClick={() => setOpenSection(openSection === 'pricing' ? null : 'pricing')}
          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 16px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>
          Pricing Overview
          {openSection === 'pricing' ? <ChevronDown size={14} style={{ marginLeft: 'auto' }} /> : <ChevronRight size={14} style={{ marginLeft: 'auto' }} />}
        </button>
        {openSection === 'pricing' && (
          <div style={{ padding: '12px 16px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderTop: 'none', borderRadius: '0 0 8px 8px' }}>
            {PRICING.map(p => (
              <div key={p.service} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border-color)' }}>
                <span style={{ fontSize: '13px' }}>{p.service}</span>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#FF6A00' }}>{p.price}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{p.note}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
